import React from "react";
import PropTypes from "prop-types";
import { DynamicField } from "@totalsoft/rocket-ui";
import { defaultNumericPlaces, defaultDecimalPlaces } from "constants/numericLimits";
import { testPhone } from "utils/pure-validation/primitiveValidators";
import PhoneNumberField from "components/common/PhoneNumberTextField";
import { emptyString } from "constants/variousTypes";
import { getDynamicFieldProperties } from "utils/functions";

const PhoneNumberFieldComponent = ({
  column,
  item,
  index,
  onPropertyChange,
  hasWritePermission,
  countryCode,
  sectionIdx,
  t
}) => {
  return {
    Component: PhoneNumberField,
    ComponentProps: {
      defaultCountry: countryCode,
      onChange: e => onPropertyChange(index, column.propertyName, e, sectionIdx),
      disabled: !hasWritePermission,
      value: item[column.propertyName],
      error:
        (!testPhone(String(item[column.propertyName]), countryCode) && item[column.propertyName]) ||
        (column.required && !item[column.propertyName]),
      helperText:
        !testPhone(String(item[column.propertyName]), countryCode) && item[column.propertyName]
          ? t("Validations.Generic.Phone")
          : column.required && !item[column.propertyName]
          ? t("Validations.Generic.Mandatory")
          : emptyString,
      country: countryCode
    }
  };
};

const CustomComponents = ({
  column,
  item,
  index,
  onPropertyChange,
  hasWritePermission,
  countryCode,
  sectionIdx,
  t
}) => {
  const { propertyType } = column;

  switch (propertyType) {
    case "PHONE_NUMBER":
      return PhoneNumberFieldComponent({
        column,
        item,
        index,
        onPropertyChange,
        hasWritePermission,
        countryCode,
        sectionIdx,
        t
      });
    default:
      return null;
  }
};

const PartnerExtraInformationWrapper = ({
  column,
  item,
  index,
  onPropertyChange,
  hasWritePermission,
  countryCode,
  sectionIdx,
  t,
  loadPropertyDetails
}) => {
  const customComponent = CustomComponents({
    column,
    item,
    index,
    onPropertyChange,
    hasWritePermission,
    countryCode,
    sectionIdx,
    t
  });

  return (
    <DynamicField
      id={"extraInfoProperty" + index}
      {...getDynamicFieldProperties(column.propertyType)}
      fullWidth
      onChange={e => onPropertyChange(index, column.propertyName, e, sectionIdx)}
      value={item[column.propertyName]}
      valueKey={"id"}
      labelKey={"value"}
      disabled={!hasWritePermission}
      required={column.required}
      error={column.required && !item[column.propertyName]}
      helperText={column.required && !item[column.propertyName] ? t("Validations.Generic.Mandatory") : emptyString}
      inputProps={{
        decimalScale: column.propertyType === "INT" ? defaultNumericPlaces : defaultDecimalPlaces
      }}
      simpleValue={true}
      CustomComponent={customComponent?.Component}
      customComponentProps={customComponent?.ComponentProps}
      loadOptions={() => loadPropertyDetails(column.propertyCode)}
      isClearable={column.propertyType === "ADAPTIVE_LOOKUP" || column.propertyType === "TP_DATE"}
      sx={{ marginBottom: 1, marginTop: 1, display: "block", marginLeft: "0.5rem" }}
    />
  );
};

PartnerExtraInformationWrapper.propTypes = {
  column: PropTypes.shape({
    propertyName: PropTypes.string.isRequired,
    propertyType: PropTypes.string.isRequired,
    caption: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired,
    propertyCode: PropTypes.string.isRequired
  }).isRequired,
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  hasWritePermission: PropTypes.bool.isRequired,
  countryCode: PropTypes.string,
  sectionIdx: PropTypes.number,
  t: PropTypes.func.isRequired,
  loadPropertyDetails: PropTypes.func.isRequired
};

PhoneNumberFieldComponent.propTypes = {
  column: PropTypes.shape({
    propertyName: PropTypes.string.isRequired,
    propertyType: PropTypes.string.isRequired,
    caption: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired,
    propertyCode: PropTypes.string.isRequired
  }),
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  hasWritePermission: PropTypes.bool.isRequired,
  countryCode: PropTypes.string,
  sectionIdx: PropTypes.number,
  t: PropTypes.func.isRequired
};

CustomComponents.propTypes = {
  column: PropTypes.shape({
    propertyType: PropTypes.string.isRequired
  }),
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  hasWritePermission: PropTypes.bool.isRequired,
  countryCode: PropTypes.string,
  sectionIdx: PropTypes.number,
  t: PropTypes.func.isRequired
};

export default PartnerExtraInformationWrapper;
